/**
 * Marco de la aplicacion.
 *
 * Un solo componente resuelve las cuatro formas de pantalla que pidio el
 * negocio, sin duplicar la navegacion:
 *
 *   - Movil (<640px): barra inferior fija, al alcance del pulgar.
 *   - Tablet / iPad: la misma barra inferior, con mas columnas de contenido.
 *   - Escritorio y all-in-one (>=1024px): barra lateral fija y mas densidad.
 *
 * La barra inferior lleva `pb-safe` porque en iPhone la franja del sistema se
 * come los ultimos 34 px de la pantalla y taparia los botones.
 */

import { useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BarChart3,
  Boxes,
  Building2,
  Check,
  ChevronDown,
  HandCoins,
  LogOut,
  MoreHorizontal,
  Moon,
  Receipt,
  ScanBarcode,
  ScanLine,
  Sparkles,
  Store,
  Sun,
  Users,
  Wallet,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { ROLE_LABELS, type Permission } from '@abarrotes/shared';
import { ROUTES } from '../routes.js';
import { isActivePath, Link, useRouter } from '../lib/router.js';
import { useSession } from '../lib/session.js';
import { useCameraScanAvailable } from '../lib/scanner.js';
import { requestCameraScan } from '../lib/scan-request.js';
import { RealtimeContext, useRealtime, type ConnectionState } from '../lib/realtime.js';
import { useHideOnScroll, usePublishedHeight } from '../lib/use-hide-on-scroll.js';
import { useViewportGuard } from '../lib/use-viewport-guard.js';
import { useTheme } from '../lib/theme.js';
import { makeDiagTapHandler } from '../lib/diag.js';
import { useBranding } from '../lib/branding.js';
import { AziruSignature } from './AziruSignature.js';
import { BrandLogo } from './BrandLogo.js';
import { NotificationBell } from './NotificationBell.js';
import { SeedRain } from './SeedRain.js';
import { Button, cx, Modal } from './ui.js';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  permission: Permission;
  /**
   * Se ESCONDE a quien tenga este otro permiso, porque ya llega ahi por dentro
   * de otra pantalla y el renglon solo seria una segunda puerta al mismo cuarto.
   *
   * No es lo mismo que quitar la direccion: la pantalla sigue viva y sigue
   * siendo la unica entrada de quien NO tiene ese permiso. Un menu que se acorta
   * para el dueño no puede acortarse tambien para el cajero, que llega por aqui
   * o no llega.
   */
  supersededBy?: Permission;
  /** Aparece en la barra inferior del movil; el resto va en "Mas". */
  primary?: boolean;
}

const NAV: NavItem[] = [
  { to: ROUTES.pos, label: 'Caja', icon: <ScanLine className="size-5" />, permission: 'sale:create', primary: true },
  { to: ROUTES.dashboard, label: 'Panel', icon: <BarChart3 className="size-5" />, permission: 'report:operations', primary: true },
  { to: ROUTES.intelligence, label: 'Surtido', icon: <Sparkles className="size-5" />, permission: 'intelligence:view', primary: true },
  /*
   * `inventory:adjust` y NO `product:view`, que es lo que decia hasta el 14 de
   * agosto de 2026.
   *
   * Inventario es donde se TRABAJA el catalogo: se ajusta la existencia, se
   * registra la merma, se resurte y se cambia el precio. Poder LEER el catalogo
   * es otra cosa — es lo que necesita el buscador de la Caja para encontrar lo
   * que se esta cobrando— y el empleado lo tiene por eso, no para abrir esta
   * pantalla. Con `product:view` aqui, el unico rol que solo cobra entraba a ver
   * el inventario entero.
   */
  { to: ROUTES.inventory, label: 'Inventario', icon: <Boxes className="size-5" />, permission: 'inventory:adjust', primary: true },
  { to: ROUTES.credit, label: 'Credito', icon: <HandCoins className="size-5" />, permission: 'customer:view' },
  // Los gastos viven al final del Panel: quien lo abre captura, borra y ve el
  // reparto sin salir de ahi, y el renglon aqui solo repetiria el destino. El
  // CAJERO no tiene `report:operations`, o sea que para el este renglon es la
  // unica entrada — y es quien saca el dinero del cajon para el flete y las
  // bolsas. Sin capturarlo, el corte de su turno sale sobrado.
  { to: ROUTES.expenses, label: 'Gastos', icon: <Receipt className="size-5" />, permission: 'expense:create', supersededBy: 'report:operations' },
  // `cash:close` y NO `cash:open`: el Corte es donde se CIERRA. El empleado abre
  // el cajon —desde la Caja, para no esperar a nadie a las siete— pero contar el
  // dinero y firmar la diferencia no es suyo, asi que esta pantalla tampoco.
  { to: ROUTES.cash, label: 'Corte', icon: <Wallet className="size-5" />, permission: 'cash:close' },
  // `sale:create` y no `sale:view_all`: el candado de QUE ventas se ven ya lo
  // pone la RLS —`user_id = auth.uid() or has_store_permission('sale:view_all')`—
  // asi que quien cobra ve las suyas y el dueño las de todos, sin que el menu
  // tenga que decidirlo. Con `sale:view_all` aqui, el que esta en el mostrador
  // no podia abrir la pantalla donde estan SUS PROPIAS ventas ni el boton de
  // gasto del dia. El encabezado dice de quien son las que se estan viendo.
  { to: ROUTES.sales, label: 'Ventas', icon: <Receipt className="size-5" />, permission: 'sale:create' },
  // Es la pantalla de la dueña: quien trabaja aqui, que hizo cada quien, que
  // esta esperando su firma, y como se llama la sucursal.
  { to: ROUTES.staff, label: 'Personal', icon: <Users className="size-5" />, permission: 'user:manage' },
  // Las sucursales del negocio. Se toca al abrir una y casi nunca despues, asi
  // que va al final y sin `primary`: no le quita un lugar de la barra inferior
  // del telefono a algo que se usa a diario.
  { to: ROUTES.stores, label: 'Sucursales', icon: <Building2 className="size-5" />, permission: 'store:manage' },
];

/**
 * Que permiso exige cada pantalla, para el candado por direccion de `App.tsx`.
 *
 * Se DERIVA de `NAV` en vez de escribirse aparte: dos listas del mismo hecho
 * acaban discrepando, y aqui discrepar significa una pantalla que el menu
 * esconde y la barra de direcciones abre. Hasta el 14 de agosto de 2026 no habia
 * candado ninguno —`renderRoute` era un `switch` pelado— asi que teclear
 * `/personal` montaba la administracion de personal completa para cualquiera. No
 * era escalacion, porque las RPC contestan `FORBIDDEN`, pero se veia como una
 * pantalla que falla campo por campo en vez de una puerta cerrada.
 *
 * SE MIRA `permission` Y NUNCA `supersededBy`. Son cosas distintas:
 * `supersededBy` esconde un RENGLON del menu a quien ya llega a esa pantalla por
 * otro lado —la dueña no ve "Gastos" porque entra desde el Panel— y usarlo aqui
 * la dejaria fuera de su propia pantalla de gastos.
 */
export const ROUTE_PERMISSION: Record<string, Permission | undefined> = Object.fromEntries(
  NAV.map((item) => [item.to, item.permission]),
);

export function Shell({ children }: { children: ReactNode }): ReactNode {
  const session = useSession();
  const connection = useRealtime(session.storeId);
  const [moreOpen, setMoreOpen] = useState(false);
  const { path } = useRouter();

  const columnRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);

  // La altura real de la barra viaja como variable CSS hasta el relleno de
  // `main`; el encabezado esta fuera del flujo y si no, el contenido nace
  // debajo del vidrio.
  usePublishedHeight(headerRef, columnRef, '--app-header-h');
  const headerHidden = useHideOnScroll(mainRef, headerRef, path);

  // El teclado de iOS desplaza la ventana entera y no siempre la devuelve; sin
  // esto la barra inferior se queda subida con una franja muerta debajo.
  useViewportGuard();

  const allowed = NAV.filter(
    (item) =>
      session.can(item.permission) &&
      !(item.supersededBy !== undefined && session.can(item.supersededBy)),
  );
  const primary = allowed.filter((item) => item.primary).slice(0, 4);
  const secondary = allowed.filter((item) => !primary.includes(item));

  return (
    /*
     * Marco de altura fija con UNA sola region que se desplaza (`main`).
     *
     * Esto es lo que separa una aplicacion de una pagina web: los bordes
     * —barra lateral, encabezado, barra inferior— no se mueven nunca, y el
     * contenido se desplaza dentro de su marco. Si el contenedor raiz creciera
     * con el contenido, el pie del ticket con el boton de cobrar acabaria
     * fuera de la pantalla justo cuando el ticket se hace largo, que es
     * exactamente cuando mas se necesita.
     */
    <div className="h-app overflow-hidden bg-bg text-ink flex">
      {/* Barra lateral: solo escritorio y all-in-one. */}
      <aside className="hidden lg:flex w-60 xl:w-64 shrink-0 h-full min-h-0 flex-col border-r border-line bg-surface pt-safe pb-safe pl-safe">
        {/*
          El rincon del logotipo lleva la lluvia de fondo, en su version chica.
          `relative` y `overflow-hidden` son lo unico que se agrega al marco: la
          capa va posicionada en absoluto y no ocupa lugar, asi que la altura del
          bloque —y con ella el reparto del resto de la barra— no se mueve. La
          marca va despues en el documento, por lo que queda encima sin repartir
          `z-index` por toda la pantalla.

          NO se extiende al resto de la barra lateral: debajo va la lista de
          navegacion, y poner algo moviendose detras de las palabras que el
          cajero lee para cambiar de pantalla es exactamente lo que esta lluvia
          no debe hacer.
        */}
        {/*
          La campanita va AQUI, en el rincon de la marca, y no abajo con el
          resto de los controles: en una aplicacion sin barra superior, la
          esquina de arriba del marco ES la barra superior, y es donde la mano
          va a buscarla sin que nadie se lo explique.

          `-my-1.5` no es cosmetico: el area que se toca mide 48 px y el bloque
          tiene 20 de relleno arriba y abajo, asi que sin eso el rincon crecia
          16 px y empujaba la navegacion entera hacia abajo. Con el margen
          negativo la campanita se mete en un relleno que estaba vacio de todos
          modos: el area tactil sigue midiendo 48, y el bloque solo crece 4.
        */}
        <div className="relative overflow-hidden px-5 py-5 shrink-0">
          <SeedRain variant="brand" />
          <div className="relative flex items-center justify-between gap-2">
            <Brand />
            <NotificationBell className="-my-1.5 -mr-1.5" />
          </div>
        </div>

        {/*
          El escaneo con camara tambien vive aqui, y no solo en el encabezado
          del telefono, por un aparato concreto: el iPad ACOSTADO mide 1366 px
          de ancho, o sea que entra por `lg` y ve esta barra lateral en lugar
          del encabezado. Sin este boton, girar el iPad hacia el paisaje hacia
          desaparecer el escaner — que es de los aparatos para los que mas se
          pidio. `ScanButton` se esconde solo donde no hay pantalla tactil, asi
          que la all-in-one de raton y teclado sigue sin verlo.
        */}
        <div className="px-3 shrink-0 space-y-2">
          <StorePicker />
          <ScanButton withLabel />
        </div>

        {/* Si algun dia hay mas secciones que altura, se desplaza solo esta lista. */}
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
          {allowed.map((item) => (
            <SidebarLink key={item.to} item={item} />
          ))}
        </nav>

        <div className="px-3 pb-4 space-y-2 shrink-0">
          {/*
            La firma de quien hizo el sistema comparte RENGLON con el estado del
            canal, y va del otro lado.

            Antes era un renglon propio —el glifo y "Hecho por Aziru" escrito—,
            y en el pie de la barra eso dejaba tres bloques apilados diciendo
            cosas de tres asuntos distintos. La firma no es informacion de la
            tienda: es la placa del autor, y una placa se pone en una esquina, no
            en la lista. Compartiendo renglon, el pie vuelve a ser dos bloques
            —el estado y la persona— y la firma queda de canto, a la derecha,
            con todo el hueco del renglon separandola.

            El glifo solo, sin la palabra: en escritorio hay tiempo de pasar el
            raton por encima, y ahi el titulo dice de quien es. Ver
            `AziruSignature`.
          */}
          <div className="flex items-center justify-between gap-2">
            <ConnectionBadge state={connection} />
            {/* `mr-1` alinea el GLIFO —no su cuadro— con el filo derecho de la
                tarjeta de abajo: el cuadro mide 32 y la marca va centrada, asi
                que sin ese ajuste el ojo la ve metida hacia adentro. */}
            <AziruSignature className="mr-1" />
          </div>
          <UserBar />
        </div>
      </aside>

      <div ref={columnRef} className="relative flex-1 min-w-0 h-full min-h-0 flex flex-col">
        {/*
          Encabezado movil y tablet.

          Va en `absolute` —dentro de esta columna, NO `fixed` contra la
          ventana— porque se aparta al bajar. Sacarlo del flujo es lo que
          permite moverlo con `transform`: el contenido no se recoloca y la
          barra se desliza sin arrastrar la lista bajo el dedo.

          Sigue sin necesitar calculos a mano: el area segura la pone `pt-safe`
          aqui mismo, y el hueco que deja de ocupar lo reserva `main` con la
          altura MEDIDA de esta barra (`--app-header-h`).

          `z-30` lo deja por encima del contenido que se desplaza debajo del
          vidrio, y por debajo de las hojas (`z-50`) y los avisos (`z-60`).
        */}
        <header
          ref={headerRef}
          className={cx(
            'lg:hidden absolute inset-x-0 top-0 z-30 glass border-b border-line pt-safe px-safe',
            'will-change-transform transition-transform duration-300 ease-[var(--ease-out-soft)]',
            'motion-reduce:transition-none',
            headerHidden ? '-translate-y-full' : 'translate-y-0',
          )}
        >
          <div className="h-14 px-4 flex items-center justify-between gap-3">
            {/*
              El sello de la tienda y la firma de quien hizo el sistema viajan
              juntos, pegados a la izquierda: sueltos, el `justify-between` de
              la fila habria dejado la firma flotando en medio del encabezado,
              que es donde mas estorba y donde menos se lee como una firma.
            */}
            <div className="flex items-center gap-2.5 shrink-0">
              <Brand compact />
              <AziruSignature className="max-[359px]:hidden" />
            </div>
            {/* El orden es el de la mano derecha: primero lo que se usa a cada
                rato (escanear), luego lo que se consulta (avisos), y al final
                el estado y la sucursal, que casi nunca se tocan. */}
            <div className="flex items-center gap-2 min-w-0">
              <ScanButton />
              <NotificationBell />
              <ConnectionDot state={connection} />
              <StorePicker compact />
            </div>
          </div>
        </header>

        {/* `overscroll-contain` evita que al llegar al final de una lista el
            gesto arrastre la pagina de atras, que se siente a navegador. */}
        {/* El estado del socket baja por contexto: las pantallas lo leen para
            su indicador sin abrir una conexion propia. */}
        {/*
          Los margenes laterales del sistema se aplican AQUI, en el unico
          contenedor por el que pasan todas las pantallas, y no ocho veces en
          cada una.
          El caso que lo obliga es el telefono ACOSTADO: en vertical los
          margenes valen cero, pero al girar el aparato la muesca se pasa a un
          costado y se lleva 44 px de la columna. Sin esto, la primera letra de
          cada renglon —y el primer boton de cada fila— quedan debajo del vidrio
          negro.
          `lg:pl-0` porque a partir de ahi la barra lateral es la que toca el
          borde izquierdo y ya trae su propio `pl-safe`; dejarlo puesto sumaria
          el margen dos veces y abriria un hueco en medio de la pantalla.
        */}
        {/*
          `overflow-x-hidden` NO sobra, y es la diferencia entre una aplicacion y
          una pagina web mal cuadrada.

          Al poner `overflow-y: auto`, la especificacion obliga al eje X a dejar
          de ser `visible` y volverse `auto` tambien. O sea que este contenedor
          desplazaba de lado, y cualquier desborde de una sola pantalla —una fila
          de botones que no cupo, una tarjeta que se estiro— convertia el sistema
          entero en algo que se arrastra horizontalmente: el encabezado y la barra
          inferior quietos, el contenido corrido, los titulos cortados por la
          izquierda. Se veia exactamente como una pantalla "que baila".

          Recortando el eje X eso es imposible por construccion. Las tablas
          anchas siguen desplazandose donde deben: dentro de su propio
          envoltorio `overflow-x-auto`.
        */}
        {/*
          `pt-[var(--app-header-h)]` reserva el sitio del encabezado, que ya no
          ocupa lugar por estar fuera del flujo. Es relleno DENTRO de la region
          que se desplaza, no un margen del marco: al bajar se va con el
          contenido, y por eso la lista pasa por debajo del vidrio en vez de
          cortarse contra un borde. En escritorio la barra no existe, la
          variable vale 0px y esto no suma nada.
        */}
        <main
          ref={mainRef}
          className="flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-safe lg:pl-0 pt-[var(--app-header-h,0px)]"
        >
          <RealtimeContext.Provider value={connection}>{children}</RealtimeContext.Provider>
        </main>

        {/* Barra inferior: movil y tablet. Es un hermano del contenido, no un
            elemento flotante, asi que no hay que reservarle espacio a mano ni
            calcular solapamientos con el area segura del iPhone. */}
        <nav className="lg:hidden shrink-0 border-t border-line bg-surface pb-nav px-safe">
          <div className="grid grid-flow-col auto-cols-fr">
            {primary.map((item) => (
              <TabLink key={item.to} item={item} />
            ))}
            {secondary.length > 0 ? (
              <button
                type="button"
                onClick={() => setMoreOpen(true)}
                className="touch-target flex flex-col items-center justify-center gap-0.5 text-subtle"
              >
                <MoreHorizontal className="size-5" />
                <span className="text-[11px] font-medium">Mas</span>
              </button>
            ) : null}
          </div>
        </nav>
      </div>

      <Modal open={moreOpen} onClose={() => setMoreOpen(false)} title="Mas opciones" size="sm">
        <div className="space-y-1">
          {secondary.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setMoreOpen(false)}
              className="flex items-center gap-3 rounded-xl px-3 py-3 text-ink hover:bg-surface-2"
            >
              {item.icon}
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-line space-y-2">
          <ThemeToggle block />
          <Button
            variant="ghost"
            block
            onClick={() => {
              setMoreOpen(false);
              void session.logout();
            }}
          >
            <LogOut className="size-4" />
            Cerrar sesion
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function Brand({ compact = false }: { compact?: boolean }): ReactNode {
  const branding = useBranding();
  /*
   * Cinco toques en el sello encienden o apagan el diagnostico.
   *
   * Es la UNICA via en la aplicacion instalada: ahi no hay barra de direcciones
   * para escribir `?diag=1`, y en iOS su almacenamiento esta separado del de
   * Safari, asi que encenderlo alla no sirve de nada aca. Ver `lib/diag.ts`.
   *
   * Va sobre el sello y no sobre un boton propio porque no debe existir un
   * control visible: cinco toques seguidos en un logotipo no le pasan a nadie
   * por accidente, y se explica por telefono en una frase.
   */
  const tocarDiag = useMemo(() => makeDiagTapHandler(), []);

  return (
    /*
     * COMPACTO NO SE ENCOGE; con nombre, SI.
     *
     * Con nombre —la barra lateral— tiene que poder encogerse, porque es lo que
     * hace que el `truncate` del texto funcione. Compacto —el encabezado del
     * telefono— es solo el sello, y ahi encogerse no recorta nada: el sello
     * lleva `shrink-0` y se sale de su envoltorio, comiendose el hueco que lo
     * separa del resto de la barra. Medido en un aparato de 320 px con la
     * campanita puesta: el envoltorio quedaba en 28.31 px y el sello seguia
     * midiendo 32, o sea 3.7 px de aire perdidos entre la marca y los botones.
     * Con esto, lo que falta lo cede el selector de sucursal, que para eso
     * trunca.
     */
    <div className={cx('flex items-center gap-2.5', compact ? 'shrink-0' : 'min-w-0')}>
      {/* El sello trae su propia placa; no lleva recuadro de color alrededor.
          Se anima solo al cargar la aplicacion, no en cada re-dibujo. */}
      {/*
        El gesto va en un envoltorio y no en `BrandLogo`: ese componente lo
        pintan tambien el ticket, el corte y la pantalla de acceso, y no tiene
        por que cargar con un manejador que solo le sirve al marco.

        `aria-hidden` y sin `role`: NO es un control. Es un gesto escondido, y
        anunciarlo a un lector de pantalla como boton seria ofrecer una puerta
        que no lleva a ningun lado util para quien lo use.
      */}
      <span onClick={tocarDiag} className="contents" aria-hidden>
        <BrandLogo className="size-8 shrink-0" animated />
      </span>
      {!compact ? (
        <span className="font-semibold tracking-tight truncate">{branding.shortName}</span>
      ) : null}
    </div>
  );
}

function SidebarLink({ item }: { item: NavItem }): ReactNode {
  const { path } = useRouter();
  const active = isActivePath(path, item.to);

  return (
    <Link
      to={item.to}
      className={cx(
        'flex items-center gap-3 rounded-xl px-3 h-11 text-[15px] transition-colors',
        active
          ? 'bg-primary-soft text-primary font-semibold'
          : 'text-muted hover:text-ink hover:bg-surface-2',
      )}
    >
      {item.icon}
      <span className="truncate">{item.label}</span>
    </Link>
  );
}

function TabLink({ item }: { item: NavItem }): ReactNode {
  const { path } = useRouter();
  const active = isActivePath(path, item.to);

  return (
    <Link
      to={item.to}
      className={cx(
        /*
         * Sin `py-2`. La banda de la barra la fija `touch-target` en 48 px, que
         * es el minimo tactil de Apple y de Material; el relleno se sumaba
         * encima y la dejaba en 57. Con la franja del gesto del iPhone debajo, la
         * barra media 92 px y quedaban 42 px muertos por debajo de la etiqueta
         * contra 8 por encima del icono: el contenido se veia flotando en la
         * mitad de arriba de una barra demasiado alta, despegada del borde.
         * En 48 el total queda en 82 px, que es la medida de la barra nativa de
         * iOS (49 pt de banda + 34 de indicador).
         */
        'touch-target flex flex-col items-center justify-center gap-0.5 transition-colors',
        active ? 'text-primary' : 'text-subtle',
      )}
    >
      {item.icon}
      <span className="text-[11px] font-medium">{item.label}</span>
    </Link>
  );
}

/**
 * Escanear con la camara.
 *
 * Vive en el MARCO y no en la caja porque desde aqui sirve en las ocho
 * pantallas: el dueño que esta revisando el surtido y quiere ver un producto no
 * tiene que ir primero a la caja para poder apuntar.
 *
 * Se esconde donde no tiene sentido —una computadora de raton y teclado, donde
 * el lector USB es mejor herramienta— pero NO se esconde cuando la camara puede
 * fallar. Que el permiso este denegado, que la direccion no sea segura o que no
 * haya camara se explica adentro, con su motivo: un boton que desaparece deja a
 * quien lo busca convencido de que el sistema no lo trae.
 *
 * No abre la camara: navega a la caja y deja el recado. El por que esta en
 * `lib/scan-request.ts`.
 *
 * EL SIMBOLO ES `ScanBarcode` Y NO `ScanLine`. Los dos son el marco de esquinas
 * del escaneo, pero `ScanLine` ya es el icono de la pestaña "Caja" que esta a
 * unos centimetros: repetirlo obliga a leer para distinguir un destino de una
 * accion. `ScanBarcode` lleva barras dentro del marco, o sea que dice de un
 * vistazo lo unico que hace este boton — leer un codigo de barras.
 */
/** Trazo mas fino que el de fabrica (2). El simbolo se ve dibujado, no estampado. */
const SCAN_STROKE = 1.75;

function ScanButton({ withLabel = false }: { withLabel?: boolean }): ReactNode {
  const available = useCameraScanAvailable();
  const { navigate } = useRouter();
  const { can } = useSession();

  if (!available || !can('sale:create')) return null;

  const start = (): void => {
    navigate(ROUTES.pos);
    requestCameraScan();
  };

  if (withLabel) {
    return (
      <Button variant="secondary" block onClick={start}>
        <ScanBarcode className="size-4" strokeWidth={SCAN_STROKE} />
        Escanear
      </Button>
    );
  }

  return (
    /*
     * DOS MEDIDAS, A PROPOSITO. El area que se toca mide 48 px —la guia tactil
     * de Apple y Material— y lo que se PINTA mide 36, igual que el selector de
     * sucursal que tiene al lado. Un cuadro pintado de 48 junto a una pastilla
     * de 36 se lee como un error de alineacion.
     *
     * Y EL MISMO TRAJE QUE EL SELECTOR: borde `line` sobre `surface-2`, no una
     * placa de color. Antes iba en verde de marca y era el elemento mas fuerte
     * del encabezado, gritando por encima del logotipo y del nombre de la
     * sucursal. Lo que hace que una barra se vea cara no es que cada control
     * pida turno, sino que todos hablen igual: aqui el encabezado es una sola
     * fila de piezas del mismo material, y el color queda reservado para lo que
     * de verdad informa —el punto de "en vivo"—. El realce se guarda para el
     * dedo: al presionar, el cuadro se encoge apenas y el borde se marca.
     */
    <button
      type="button"
      onClick={start}
      aria-label="Escanear con la camara"
      className="group touch-target grid place-items-center shrink-0"
    >
      <span
        className={cx(
          'grid place-items-center size-9 rounded-xl',
          'border border-line bg-surface-2 text-ink',
          'transition-[border-color,transform] duration-150',
          'group-hover:border-line-strong group-active:scale-95',
        )}
      >
        {/*
          18 px y no los 20 de `size-5`: dentro de un cuadro de 36 deja 9 px de
          aire por lado en vez de 8, y con el trazo mas fino el simbolo se lee
          dibujado y no estampado. Es la diferencia entre un icono metido en una
          caja y uno hecho para ella.
        */}
        <ScanBarcode className="size-[18px]" strokeWidth={SCAN_STROKE} />
      </span>
    </button>
  );
}

/**
 * Selector de sucursal. Con una sola tienda no se muestra, para no ocupar
 * espacio con un control que nunca se usa.
 */
function StorePicker({
  compact = false,
  className,
}: {
  compact?: boolean;
  className?: string;
}): ReactNode {
  const { user, storeId, storeName, setStoreId } = useSession();
  const [open, setOpen] = useState(false);

  const stores = user?.stores ?? [];
  if (stores.length <= 1) {
    return compact ? null : (
      <div className={cx('px-3 py-2 text-[13px] text-muted truncate', className)}>{storeName}</div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cx(
          'flex items-center gap-2 rounded-xl border border-line bg-surface-2 px-3',
          'text-[13px] font-medium text-ink hover:border-line-strong transition-colors',
          // `min-w-0` en la version compacta: es el ULTIMO de cuatro controles
          // en el encabezado del telefono, y sin el su minimo automatico es el
          // nombre de la sucursal SIN PARTIR. En un aparato de 320 px eso
          // empujaba la fila por encima de la pantalla. Con esto se encoge y el
          // `truncate` de adentro hace su trabajo (§9).
          compact ? 'h-9 min-w-0 max-w-[9.5rem]' : 'h-11 w-full',
          className,
        )}
      >
        <Store className="size-4 text-muted shrink-0" />
        <span className="truncate">{storeName}</span>
        <ChevronDown className="size-4 text-muted shrink-0 ml-auto" />
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Cambiar de sucursal" size="sm">
        <div className="space-y-1">
          {stores.map((store) => (
            <button
              key={store.storeId}
              type="button"
              onClick={() => {
                setStoreId(store.storeId);
                setOpen(false);
              }}
              className={cx(
                'w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left',
                store.storeId === storeId ? 'bg-primary-soft text-primary' : 'hover:bg-surface-2',
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{store.storeName}</p>
                {/* `ROLE_LABELS` y no un ternario a mano, que es lo que habia:
                    la cadena terminaba en 'Cajero' para todo lo demas, asi que
                    un EMPLEADO se veia aqui como "Cajero" mientras el resto de
                    la aplicacion lo llamaba "Empleado". El mismo defecto que ya
                    se habia corregido en `UserBar`, cincuenta lineas abajo, y
                    que este rincon se quedo arrastrando. */}
                <p className="text-[12px] text-muted">
                  {store.storeCode} · {ROLE_LABELS[store.role]}
                </p>
              </div>
              {store.storeId === storeId ? <Check className="size-5 shrink-0" /> : null}
            </button>
          ))}
        </div>
      </Modal>
    </>
  );
}

/**
 * Estado del canal en vivo.
 *
 * Se muestra siempre, no solo cuando falla: en una tienda es importante saber
 * de un vistazo si lo que estas viendo esta al dia o congelado.
 */
function ConnectionBadge({ state }: { state: ConnectionState }): ReactNode {
  const config = {
    online: { icon: <Wifi className="size-4" />, text: 'En vivo', cls: 'text-primary' },
    connecting: { icon: <Wifi className="size-4" />, text: 'Conectando...', cls: 'text-muted' },
    offline: { icon: <WifiOff className="size-4" />, text: 'Sin conexion', cls: 'text-warn' },
  }[state];

  return (
    <div className={cx('flex items-center gap-2 px-3 py-2 text-[12px] font-medium', config.cls)}>
      {config.icon}
      {config.text}
    </div>
  );
}

function ConnectionDot({ state }: { state: ConnectionState }): ReactNode {
  const cls =
    state === 'online' ? 'bg-primary' : state === 'connecting' ? 'bg-muted' : 'bg-warn';
  const label =
    state === 'online' ? 'En vivo' : state === 'connecting' ? 'Conectando' : 'Sin conexion';

  return (
    <span
      title={label}
      aria-label={label}
      className={cx('size-2 rounded-full shrink-0', cls, state === 'online' && 'animate-pulse')}
    />
  );
}

function UserBar(): ReactNode {
  const { user, role, logout } = useSession();

  return (
    <div className="rounded-xl border border-line bg-surface-2 p-3">
      <p className="text-[13px] font-medium truncate">{user?.fullName}</p>
      {/*
        El puesto sale de `ROLE_LABELS`, que es el espejo de la matriz, y no de
        un `if` escrito aqui. El que habia traducia a mano tres roles y se quedo
        atras dos veces: seguia diciendo "Cajero" cuando esa etiqueta ya era
        "Caja (gerente)", y a un empleado tambien lo llamaba cajero, porque era
        el ultimo caso del encadenado.
      */}
      <p className="text-[12px] text-muted">{role ? ROLE_LABELS[role] : ''}</p>
      <div className="mt-2 flex gap-2">
        <ThemeToggle />
        <Button size="sm" variant="ghost" onClick={() => void logout()} title="Cerrar sesion">
          <LogOut className="size-4" />
        </Button>
      </div>

    </div>
  );
}

function ThemeToggle({ block = false }: { block?: boolean }): ReactNode {
  const { theme, toggle } = useTheme();
  return (
    <Button size="sm" variant="ghost" block={block} onClick={toggle}>
      {theme === 'dark' ? <Moon className="size-4" /> : <Sun className="size-4" />}
      {block ? (theme === 'dark' ? 'Tema oscuro' : 'Tema claro') : null}
    </Button>
  );
}
